/* eslint-disable no-mixed-spaces-and-tabs */
import { Map } from "proximiio-js-library/lib/components/map/main";
import { AmenityModel } from "proximiio-js-library/lib/models/amenity";
import Feature from "proximiio-js-library/lib/models/feature";
import { FloorModel } from "proximiio-js-library/lib/models/floor";
import { PlaceModel } from "proximiio-js-library/lib/models/place";
import { AdModel } from "proximiio-js-library/lib/models/ad";
import { create } from "zustand";
import { getFloorName } from "@/lib/utils";
import {
  FilterCategoryModel,
  FilterItemModel,
} from "@/models/filterItem.model";
import { KioskModel } from "@/models/kiosk.model";
import { filterCategories } from "./data";
import { StopModel } from "@/models/stop.model";

// import { devtools } from 'zustand/middleware';
// define types for state values and actions separately
type State = {
  appSession?: string;
  appInitiated: boolean;
  authSuccess: boolean;
  dataFetched: boolean;
  map: Map;
  mapPrepared: boolean;
  mapLoaded: boolean;
  kioskMode: boolean;
  verticalKioskMode: boolean;
  kioskScaling: boolean;
  mobileMode: boolean;
  gpsMode: boolean;
  currentLang: string;
  places: PlaceModel[];
  currentPlace: PlaceModel;
  floors: FloorModel[];
  allFloors: FloorModel[];
  currentFloor: FloorModel;
  startFeature: Feature;
  finishFeature: Feature;
  routeStart: Feature | "kiosk";
  routeFinish: Feature;
  amenities: AmenityModel[];
  accessibleRoute: boolean;
  filterCategories: FilterCategoryModel[];
  activeFilter: FilterItemModel;
  showCustomRoutePicker: boolean;
  haveRouteDetails: boolean;
  haveRoutePreview: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  routeDetails: Record<string, any>;
  currentStep: number;
  activeKiosk: KioskModel | undefined;
  sidebarMinified: boolean;
  kiosks: KioskModel[];
  DBAds: AdModel[];
  ads: AdModel[];
  activeAds: AdModel[] | undefined;
  overlayContent?: "offers" | "findMyCar" | "feedback" | "cityNav" | "welcome";
  isLoading: boolean;
  forceAccessibleRoute: boolean;
  activeMenuItem: string;
  activeCategory: FilterCategoryModel;
  globalSearchOpen: boolean;
  virtualKeyboardActive: boolean;
  stepAnimated: number[];
  navMode: "mall" | "city-to" | "city-from";
  cityStart: { title: string; coordinates: number[]; poiId?: string };
  cityFinish: { title: string; coordinates: number[]; poiId?: string };
  stops: StopModel[];
  stopsBackup: StopModel[];
  activeStop: number;
  cameraRecognitionFeature: Feature;
  appIsIdle: boolean;
  haveCustomPosition: boolean;
  reachedDestination: boolean;
  hideControls: boolean;
};

type Actions = {
  setAppSession: () => void;
  setAppInitiated: (initiated: boolean) => void;
  setAuthSuccess: (auth: boolean) => void;
  setDataFetched: (fetched: boolean) => void;
  setMap: (map: Map) => void;
  setMapPrepared: (prepared: boolean) => void;
  setMapLoaded: (loaded: boolean) => void;
  setKioskMode: (isKiosk: boolean) => void;
  setVerticalKioskMode: (isVertical: boolean) => void;
  setKioskScaling: (scaling: boolean) => void;
  setMobileMode: (isMobile: boolean) => void;
  setGpsMode: (useGps: boolean) => void;
  setCurrentLang: (language: string) => void;
  setPlaces: (places: PlaceModel[]) => void;
  setCurrentPlace: (place: PlaceModel) => void;
  setFloors: (floors: FloorModel[]) => void;
  setAllFloors: (floors: FloorModel[]) => void;
  setCurrentFloor: (floor: FloorModel) => void;
  setStartFeature: (feature: Feature) => void;
  setFinishFeature: (feature: Feature) => void;
  setRouteStart: (feature: Feature | "kiosk") => void;
  setRouteFinish: (feature: Feature) => void;
  setAmenities: (amenities: AmenityModel[]) => void;
  setAccessibleRoute: ({
    accessibleRoute,
    forceAccessibleRoute,
  }: {
    accessibleRoute: boolean;
    forceAccessibleRoute?: boolean;
  }) => void;
  setActiveFilter: (activeFilter: FilterItemModel) => void;
  setShowCustomRoutePicker: (visible: boolean) => void;
  setHaveRouteDetails: (haveDetails: boolean) => void;
  setHaveRoutePreview: (havePreview: boolean) => void;
  setRouteDetails: (route: unknown) => void;
  setCurrentStep: (step: number) => void;
  setKiosks: (kiosks: KioskModel[]) => void;
  setActiveKiosk: (kiosk: KioskModel | undefined) => void;
  setDBAds: (ads: AdModel[]) => void;
  setAds: (ads: AdModel[]) => void;
  setActiveAds: (ads: AdModel[] | undefined) => void;
  setSidebarMinified: (minified: boolean) => void;
  setOverlayContent: (
    content?: "offers" | "findMyCar" | "feedback" | "cityNav" | "welcome",
  ) => void;
  setIsLoading: (isLoading: boolean) => void;
  setActiveMenuItem: (item: string) => void;
  setActiveCategory: (category: FilterCategoryModel) => void;
  setGlobalSearchOpen: (open: boolean) => void;
  setVirtualKeyboardActive: (active: boolean) => void;
  setStepAnimated: (step: number) => void;
  setNavMode: (mode: "mall" | "city-to" | "city-from") => void;
  setCityStart: (start: {
    title: string;
    coordinates: number[];
    poiId?: string;
  }) => void;
  setCityFinish: (finish: {
    title: string;
    coordinates: number[];
    poiId?: string;
  }) => void;
  setStops: (stops: StopModel[]) => void;
  setActiveStop: (stop: number) => void;
  locateMe: () => void;
  centerToFinish: () => void;
  resetView: () => void;
  reset: () => void;
  cancelRouting: () => void;
  resetAnimatedSteps: () => void;
  setCameraRecognitionFeature: (feature: Feature) => void;
  setAppIsIdle: (isIdle: boolean) => void;
  setHaveCustomPosition: (haveCustomPosition: boolean) => void;
  setReachedDestination: (reachedDestination: boolean) => void;
  setHideControls: (hideControls: boolean) => void;
};

// define the initial state
const initialState: State = {
  appSession: undefined,
  appInitiated: false,
  authSuccess: false,
  dataFetched: false,
  map: {} as Map,
  mapPrepared: false,
  mapLoaded: false,
  kioskMode: false,
  verticalKioskMode: false,
  kioskScaling: import.meta.env.VITE_WAYFINDING_KIOSK_SCALING === "true",
  mobileMode: false,
  gpsMode: import.meta.env.VITE_WAYFINDING_USE_GPS_LOCATION === "true",
  currentLang: "en",
  places: [],
  currentPlace: {} as PlaceModel,
  floors: [],
  allFloors: [],
  currentFloor: {} as FloorModel,
  startFeature: {} as Feature,
  finishFeature: {} as Feature,
  routeStart: {} as Feature,
  routeFinish: {} as Feature,
  amenities: [],
  accessibleRoute: false,
  filterCategories,
  activeFilter: {} as FilterItemModel,
  showCustomRoutePicker: false,
  haveRouteDetails: false,
  haveRoutePreview: false,
  routeDetails: {},
  currentStep: 0,
  sidebarMinified: false,
  overlayContent:
    import.meta.env.VITE_WAYFINDING_ENABLE_WELCOME === "true"
      ? "welcome"
      : undefined,
  kiosks: [],
  activeKiosk: {} as KioskModel,
  DBAds: [],
  ads: [],
  activeAds: [] as AdModel[],
  isLoading: false,
  forceAccessibleRoute: false,
  activeMenuItem: "map",
  activeCategory: {} as FilterCategoryModel,
  globalSearchOpen: false,
  virtualKeyboardActive: false,
  stepAnimated: [],
  navMode: "mall",
  cityStart: {} as { title: string; coordinates: [number, number] },
  cityFinish: {} as { title: string; coordinates: [number, number] },
  stops: [],
  stopsBackup: [],
  activeStop: 0,
  cameraRecognitionFeature: {} as Feature,
  appIsIdle: false,
  haveCustomPosition: false,
  reachedDestination: false,
  hideControls: false,
};

const autoRouting =
  import.meta.env.VITE_WAYFINDING_KIOSK_AUTO_ROUTING === "true";

const generateSessionId = () => {
  return `${Date.now()}-${Math.floor(Math.random() * 10000)}`;
};

const assignProperties = (
  poi: Feature,
  floors: FloorModel[],
  currentLang: string,
) => {
  if (poi.properties) {
    if (!poi.properties._dynamic) poi.properties._dynamic = {};
    if (!poi.geometry.type) poi.geometry.type = "Point";
    poi.properties.title =
      poi.properties.title_i18n && poi.properties.title_i18n[currentLang]
        ? poi.properties.title_i18n[currentLang]
        : poi.properties.title;
    poi.properties._dynamic.floor = poi.properties.floor_id
      ? floors.find((i) => i.id === poi.properties.floor_id)
      : null;
    poi.properties._dynamic.floorName = poi.properties._dynamic.floor
      ? getFloorName({
          floor: poi.properties._dynamic.floor,
          language: currentLang,
        })
      : null;
  }
  return poi;
};

const useMapStore = create<State & Actions>()(
  //devtools(
  (set, get) => ({
    ...initialState,
    setAppSession: () => {
      const sessionId = generateSessionId();
      console.log("SETTING APP SESSION", sessionId);
      set(() => ({ appSession: sessionId }));
    },
    setAppInitiated: (initiated) => {
      set(() => ({ appInitiated: initiated }));
    },
    setAuthSuccess: (auth) => {
      set(() => ({ authSuccess: auth }));
    },
    setDataFetched: (fetched) => {
      set(() => ({ dataFetched: fetched }));
    },
    setMap: (map) => {
      set(() => ({ map }));
    },
    setMapPrepared: (prepared) => {
      set(() => ({ mapPrepared: prepared }));
    },
    setMapLoaded: (loaded) => {
      set(() => ({ mapLoaded: loaded }));
    },
    setKioskMode: (isKiosk) => {
      set(() => ({ kioskMode: isKiosk }));
    },
    setVerticalKioskMode: (isVertical) => {
      set(() => ({ verticalKioskMode: isVertical }));
    },
    setKioskScaling: (scaling) => {
      set(() => ({ kioskScaling: scaling }));
    },
    setMobileMode: (isMobile) => {
      set(() => ({ mobileMode: isMobile }));
    },
    setGpsMode: (useGps) => {
      set(() => ({ gpsMode: useGps }));
    },
    setCurrentLang: (language) => {
      set(() => {
        let finish = get().finishFeature;
        if (finish) {
          finish = assignProperties(finish, get().floors, language);
        }
        return { currentLang: language, finishFeature: finish };
      });
    },
    setPlaces: (places) => {
      set(() => ({ places }));
    },
    setCurrentPlace: (place) => {
      set(() => ({ currentPlace: place }));
    },
    setFloors: (floors) => {
      set(() => ({ floors }));
    },
    setAllFloors: (floors) => {
      set(() => ({ allFloors: floors }));
    },
    setCurrentFloor: (floor) => {
      set(() => ({ currentFloor: floor }));
    },
    setStartFeature: (feature) => {
      set(() => {
        feature = assignProperties(feature, get().allFloors, get().currentLang);
        return { startFeature: feature };
      });
    },
    setFinishFeature: (feature) => {
      set(() => {
        feature = assignProperties(feature, get().allFloors, get().currentLang);
        return { finishFeature: feature };
      });
    },
    setRouteStart: (feature) => {
      set(() => {
        if (feature !== "kiosk") {
          feature = assignProperties(
            feature,
            get().allFloors,
            get().currentLang,
          );
        }
        return { routeStart: feature };
      });
    },
    setRouteFinish: (feature) => {
      set(() => {
        feature = assignProperties(feature, get().allFloors, get().currentLang);
        return { routeFinish: feature, finishFeature: feature };
      });
    },
    setAmenities: (amenities) => {
      set(() => ({ amenities }));
    },
    setAccessibleRoute: ({ accessibleRoute, forceAccessibleRoute }) => {
      set(() => ({ accessibleRoute, forceAccessibleRoute }));
    },
    setActiveFilter: (activeFilter) => {
      set(() => ({ activeFilter }));
    },
    setShowCustomRoutePicker: (visible) => {
      set(() => ({ showCustomRoutePicker: visible }));
    },
    setHaveRouteDetails: (haveDetails) => {
      set(() => ({ haveRouteDetails: haveDetails }));
    },
    setHaveRoutePreview: (havePreview) => {
      set(() => ({ haveRoutePreview: havePreview }));
    },
    setRouteDetails: (route) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      set(() => ({ routeDetails: route as any }));
    },
    setCurrentStep: (step) => {
      set(() => ({ currentStep: step }));
    },
    setKiosks: (kiosks) => {
      set(() => ({ kiosks }));
    },
    setActiveKiosk: (kiosk: KioskModel | undefined) => {
      set(() => ({ activeKiosk: kiosk }));
    },
    setDBAds: (ads) => {
      set(() => ({ DBAds: ads }));
    },
    setAds: (ads) => {
      set(() => ({ ads }));
    },
    setActiveAds: (ads: AdModel[] | undefined) => {
      set(() => ({ activeAds: ads }));
    },
    setSidebarMinified: (minified: boolean) => {
      set(() => ({ sidebarMinified: minified }));
    },
    setOverlayContent: (
      content?: "offers" | "findMyCar" | "feedback" | "cityNav" | "welcome",
    ) => {
      set(() => {
        if (get().verticalKioskMode && content !== undefined) {
          return { overlayContent: content, hideControls: true };
        } else if (get().verticalKioskMode && content === undefined) {
          return { overlayContent: content, hideControls: false };
        }
        return { overlayContent: content };
      });
    },
    setIsLoading: (isLoading: boolean) => {
      set(() => ({ isLoading }));
    },
    setActiveMenuItem: (item: string) => {
      set(() => ({ activeMenuItem: item }));
    },
    setActiveCategory: (category: FilterCategoryModel) => {
      set(() => ({ activeCategory: category }));
    },
    setGlobalSearchOpen: (open: boolean) => {
      set(() => ({ globalSearchOpen: open }));
    },
    setVirtualKeyboardActive: (active: boolean) => {
      set(() => ({ virtualKeyboardActive: active }));
    },
    setStepAnimated: (step: number) => {
      set(() => {
        if (get().stepAnimated.includes(step)) {
          return { stepAnimated: get().stepAnimated };
        }
        return { stepAnimated: [...get().stepAnimated, step] };
      });
    },
    setNavMode: (mode: "mall" | "city-to" | "city-from") => {
      set(() => ({ navMode: mode }));
    },
    setCityStart: (start: {
      title: string;
      coordinates: number[];
      poiId?: string;
    }) => {
      set(() => ({ cityStart: start }));
    },
    setCityFinish: (finish: {
      title: string;
      coordinates: number[];
      poiId?: string;
    }) => {
      set(() => ({ cityFinish: finish }));
    },
    setStops: (stops: StopModel[]) => {
      set(() => {
        stops = stops.map((stop) => {
          if (stop.feature) {
            assignProperties(stop.feature, get().floors, get().currentLang);
          }
          return stop;
        });
        return { stops };
      });
    },
    setActiveStop: (stop) => {
      set(() => ({ activeStop: stop }));
    },
    setCameraRecognitionFeature: (feature) => {
      set(() => ({ cameraRecognitionFeature: feature }));
    },
    setAppIsIdle: (isIdle: boolean) => {
      set(() => ({ appIsIdle: isIdle }));
    },
    setHaveCustomPosition: (haveCustomPosition) => {
      set(() => ({ haveCustomPosition }));
    },
    setReachedDestination: (reachedDestination) => {
      set(() => ({ reachedDestination }));
    },
    setHideControls: (hideControls) => {
      set(() => ({ hideControls }));
    },
    reset: () => {
      set(initialState);
    },
    locateMe: () => {
      if (Object.keys(get().map).length > 0) {
        let zoom = +import.meta.env.VITE_WAYFINDING_DEFAULT_ZOOM;
        let pitch = +import.meta.env.VITE_WAYFINDING_DEFAULT_PITCH;
        let bearing = +import.meta.env.VITE_WAYFINDING_DEFAULT_BEARING;
        let center: [number, number] = [
          +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LONGITUDE,
          +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LATITUDE,
        ];
        let level = +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LEVEL;

        /*const mapPadding = {
          top: get().mobileMode ? 50 : 130,
          bottom: get().mobileMode ? 200 : 130,
          left: get().mobileMode ? 50 : get().currentLang === "ar" ? 250 : 650,
          right: get().mobileMode ? 50 : get().currentLang === "ar" ? 650 : 250,
        };*/

        if (
          get().verticalKioskMode &&
          import.meta.env.VITE_WAYFINDING_KIOSK_SCALING === "false"
        ) {
          zoom = +import.meta.env.VITE_WAYFINDING_DEFAULT_ZOOM + 1;
        }

        if (get().kioskMode && get().activeKiosk) {
          if (get().activeKiosk?.zoom) {
            zoom = get().activeKiosk!.zoom!;
          }
          if (get().activeKiosk?.pitch) {
            pitch = get().activeKiosk!.pitch!;
          }
          if (get().activeKiosk?.bearing) {
            bearing = get().activeKiosk!.bearing!;
          }
          if (get().activeKiosk?.longitude && get().activeKiosk?.latitude) {
            center = [
              get().activeKiosk!.longitude!,
              get().activeKiosk!.latitude!,
            ];
          }
          if (get().activeKiosk?.level) {
            level = get().activeKiosk!.level!;
          }
          if (
            get().verticalKioskMode &&
            import.meta.env.VITE_WAYFINDING_KIOSK_SCALING === "false"
          ) {
            zoom = get().activeKiosk!.zoom! + 1;
          }
        }

        if (get().mobileMode && !get().haveCustomPosition) {
          zoom = 17;
          pitch = 28;
          bearing = -85;
          center = [58.40501024666028, 23.571682199999998];
        }

        if (get().haveCustomPosition) {
          const customPosition = get().map.getCustomPosition();
          if (customPosition?.coordinates) {
            center = customPosition.coordinates as [number, number];
          }
          if (customPosition?.level) {
            level = customPosition.level;
          }
        }

        if (get().routeFinish?.id) {
          level = get().routeFinish.properties.level;
          center = get().routeFinish.geometry.coordinates as [number, number];
        }

        get().map.getMapboxInstance().flyTo({
          center,
          bearing,
          pitch,
          zoom,
          // padding: mapPadding,
        });

        get().map.setFloorByLevel(level);

        console.log("locate me", center, bearing, pitch, zoom);
      }
    },
    centerToFinish: () => {
      if (Object.keys(get().map).length > 0) {
        get()
          .map.getMapboxInstance()
          .flyTo({
            center: get().routeFinish.geometry.coordinates as [number, number],
            zoom: 18,
            duration: 2000,
            essential: true,
            padding: get().verticalKioskMode
              ? { left: 0, right: 0, top: 0, bottom: 0 }
              : {
                  left: get().mobileMode
                    ? 50
                    : get().currentLang === "ar"
                      ? 100
                      : 300,
                  right: get().mobileMode
                    ? 50
                    : get().currentLang === "ar"
                      ? 300
                      : 20,
                  bottom: get().mobileMode ? 200 : 130,
                },
          });
      }
    },
    resetView() {
      const url = new URL(window.location.href);
      const urlParams = new URLSearchParams(window.location.search);

      urlParams.delete("remote_id");
      urlParams.delete("navMode");
      url.search = urlParams.toString();
      window.history.replaceState({}, "", url);

      set({
        startFeature: {} as Feature,
        finishFeature: {} as Feature,
        routeStart: get().kioskMode && autoRouting ? "kiosk" : ({} as Feature),
        routeFinish: {} as Feature,
        activeFilter: {} as FilterItemModel,
        showCustomRoutePicker: false,
        haveRouteDetails: false,
        haveRoutePreview: false,
        routeDetails: {},
        currentStep: 0,
        isLoading: false,
        overlayContent:
          get().overlayContent === "offers" ? "offers" : undefined,
        activeMenuItem: "map",
        activeCategory: {} as FilterCategoryModel,
        globalSearchOpen: false,
        stepAnimated: [],
        navMode: "mall",
        cityStart: {} as { title: string; coordinates: [number, number] },
        cityFinish: {} as { title: string; coordinates: [number, number] },
        stops: [],
        stopsBackup: [],
        activeStop: 0,
        activeAds: [] as AdModel[],
        cameraRecognitionFeature: {} as Feature,
        reachedDestination: false,
        hideControls: false,
      });
    },
    cancelRouting() {
      if (Object.keys(get().map).length > 0) {
        get().map.cancelRoute();
        get().map.showIcons();

        if (get().routeFinish?.id) {
          get().map.setFloorByLevel(get().routeFinish.properties.level);
          get()
            .map.getMapboxInstance()
            .flyTo({
              center: get().routeFinish.geometry.coordinates as [
                number,
                number,
              ],
              zoom: 18,
            });

          if (
            !get().routeFinish.properties._dynamic.polygon_id ||
            get().routeFinish.properties.metadata?.bays ||
            get().routeFinish.properties?.metadata?.prevent_polygon === true ||
            get().routeFinish.properties?.metadata?.prevent_polygon === "true"
          ) {
            get().map.disablePolygons();
          } else {
            get().map.handlePolygonSelection(get().routeFinish);
          }
        }
      }

      if (get().navMode !== "mall") {
        if (get().cityFinish?.title) {
          get()
            .map.getMapboxInstance()
            .flyTo({
              center: get().cityFinish.coordinates as [number, number],
              zoom: 18,
            });
        }
      }

      const stopsBackup = get().stops.length > 2 ? [...get().stops] : [];

      if (Object.keys(get().routeDetails).length > 0) {
        set({
          startFeature: {} as Feature,
          finishFeature: {} as Feature,
          routeStart:
            get().kioskMode && autoRouting ? "kiosk" : ({} as Feature),
          routeFinish: {} as Feature,
          showCustomRoutePicker: false,
          haveRouteDetails: false,
          routeDetails: {},
          currentStep: 0,
          activeStop: 0,
          stepAnimated: [],
          stops: [],
          stopsBackup,
          cameraRecognitionFeature: {} as Feature,
          reachedDestination: false,
        });
      } else {
        set({
          startFeature: {} as Feature,
          finishFeature: {} as Feature,
          routeStart:
            get().kioskMode && autoRouting ? "kiosk" : ({} as Feature),
          routeFinish: {} as Feature,
          showCustomRoutePicker: false,
          haveRouteDetails: false,
          routeDetails: {},
          currentStep: 0,
          stepAnimated: [],
          navMode: "mall",
          cityStart: {} as { title: string; coordinates: [number, number] },
          cityFinish: {} as { title: string; coordinates: [number, number] },
          stops: [],
          stopsBackup,
          activeStop: 0,
          cameraRecognitionFeature: {} as Feature,
          reachedDestination: false,
        });
      }
    },
    resetAnimatedSteps() {
      set({
        stepAnimated: [],
      });
    },
  }),
  //{ name: 'mapStore' }
  //)
);

export default useMapStore;
